import { adminApi } from '../api/admin';

export type PushState =
  | 'unsupported' // browser has no Push API (or iOS Safari NOT installed to home screen)
  | 'denied' // user blocked notifications
  | 'subscribed'
  | 'unsubscribed';

/** True when running as an installed home-screen app (iOS requirement). */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export async function getPushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'subscribed' : 'unsubscribed';
}

/**
 * Full enable flow: permission prompt (must be called from a user tap)
 * → browser push subscription with our VAPID key → register on the
 * backend. Throws with a human-readable message on any failure.
 */
export async function enablePush(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error(
      isIos() && !isStandalone()
        ? 'iPhone’da bildirim için önce Safari’den Paylaş → “Ana Ekrana Ekle” yap, uygulamayı oradan aç.'
        : 'Bu tarayıcı web push desteklemiyor.',
    );
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Bildirim izni verilmedi.');
  }
  const { publicKey, enabled } = await adminApi.getVapidKey();
  if (!enabled || !publicKey) {
    throw new Error('Sunucuda push yapılandırılmamış (VAPID anahtarı yok).');
  }
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyToBuffer(publicKey),
    }));
  await adminApi.subscribePush(sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } });
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await adminApi.unsubscribePush(sub.endpoint).catch(() => {});
  await sub.unsubscribe();
}

/** Decodes a URL-safe-base64 VAPID key into a plain ArrayBuffer (the
 *  type `pushManager.subscribe` wants, with no ArrayBufferLike wobble). */
function vapidKeyToBuffer(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}
